import { PlusOutlined, UploadOutlined } from '@ant-design/icons'
import { Button, FormItem, Heading, Input, TextArea } from '@lmc/ui-kit'
import { Text } from '@lmc/ui-kit/src/general'
import {
  DatePicker as AntdDatePicker,
  Form as AntdForm,
  Divider,
  Modal,
  Upload,
  type UploadFile
} from 'antd'
import dayjs, { type Dayjs } from 'dayjs'
import utc from 'dayjs/plugin/utc'
import { NAMESPACE } from 'locale'
import { serviceRecordConstants } from 'locale/serviceRecords'
import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import styled from 'styled-components'
import { MAX_DOCUMENTS } from '../constants'
import { useServiceRecordCreate } from '../hooks/useServiceRecordCreate'
import { useServiceRecordUpdate } from '../hooks/useServiceRecordUpdate'
import type {
  CreateServiceRecordInput,
  ServiceRecord,
  UpdateServiceRecordInput
} from '../types'

dayjs.extend(utc)

export type ServiceRecordModalProps = {
  isVisible: boolean
  onClose: () => void
  onSuccess: () => void
  serviceRecord?: ServiceRecord
  residentId: string
}

const ServiceRecordModal = ({
  isVisible,
  onClose,
  onSuccess,
  serviceRecord,
  residentId
}: ServiceRecordModalProps) => {
  const { t } = useTranslation(NAMESPACE.SERVICE_RECORDS)
  const [form] = AntdForm.useForm()
  const [fileList, setFileList] = useState<UploadFile[]>([])
  const isEditMode = !!serviceRecord
  const existingDocumentsCount = serviceRecord?.documents?.length || 0
  const remainingSlots = MAX_DOCUMENTS - existingDocumentsCount

  const { createServiceRecord, isCreating } = useServiceRecordCreate(() => {
    onSuccess()
  })

  const { updateServiceRecord, isUpdating } = useServiceRecordUpdate(
    residentId,
    () => {
      onSuccess()
    }
  )

  // biome-ignore lint/correctness/useExhaustiveDependencies: <explanation>
  useEffect(() => {
    if (isVisible) {
      if (serviceRecord) {
        const dateTimeValue = serviceRecord.startDate
          ? dayjs.utc(serviceRecord.startDate)
          : undefined

        form.setFieldsValue({
          provider: serviceRecord.provider || '',
          providerAddress: serviceRecord.providerAddress || '',
          description: serviceRecord.description || '',
          startDate: dateTimeValue
        })
        setFileList([])
      } else {
        form.resetFields()
        setFileList([])
      }
    }
  }, [isVisible, serviceRecord, form])

  const handleFileChange = (info: { fileList: UploadFile[] }) => {
    const maxAllowed = isEditMode ? remainingSlots : MAX_DOCUMENTS
    if (maxAllowed <= 0) {
      setFileList([])
      return
    }
    const limitedFileList = info.fileList.slice(-maxAllowed)
    setFileList(limitedFileList)
  }

  const handleSubmit = async (values: unknown) => {
    const formValues = values as {
      provider?: string
      providerAddress?: string
      description?: string
      startDate?: Dayjs | null
    }

    const toOptionalText = (value?: string): string | null | undefined => {
      if (value) return value
      return isEditMode ? null : undefined
    }

    const cleanedSubmitValues = Object.fromEntries(
      Object.entries({
        provider: formValues.provider,
        providerAddress: toOptionalText(formValues.providerAddress),
        description: toOptionalText(formValues.description),
        startDate: formValues.startDate
          ? formValues.startDate.format('YYYY-MM-DD')
          : isEditMode
            ? null
            : undefined
      } as CreateServiceRecordInput).filter(([_, value]) => value !== undefined)
    ) as CreateServiceRecordInput

    try {
      if (isEditMode && serviceRecord) {
        const existingDocumentIds = serviceRecord.documents
          ? serviceRecord.documents.map((doc) => doc.id).filter(Boolean)
          : []

        const updateInput: UpdateServiceRecordInput = {
          id: serviceRecord.id,
          existingDocumentIds,
          ...cleanedSubmitValues
        }
        await updateServiceRecord(updateInput, fileList)
      } else {
        await createServiceRecord(cleanedSubmitValues, residentId, fileList)
      }
    } catch (error) {
      console.error('Failed to submit service record:', error)
    }
  }

  if (!isVisible) return null

  return (
    <Modal
      open={isVisible}
      onCancel={onClose}
      destroyOnClose
      footer={
        <ModalFooter>
          <Button onClick={onClose}>{t(serviceRecordConstants.CANCEL)}</Button>
          <Button
            onClick={form.submit}
            variant="primary"
            isLoading={isEditMode ? isUpdating : isCreating}
          >
            {isEditMode ? (
              t(serviceRecordConstants.SAVE)
            ) : (
              <>
                <PlusOutlined />
                {t(serviceRecordConstants.ADD)}
              </>
            )}
          </Button>
        </ModalFooter>
      }
      width={600}
    >
      <Heading level={3}>{t(serviceRecordConstants.SERVICE_RECORD)}</Heading>
      <Divider />
      <StyledForm
        form={form}
        onFinish={handleSubmit}
        layout="horizontal"
        key={serviceRecord?.id || 'new'}
      >
        <FormItem
          name="provider"
          label="Provider"
          rules={[
            {
              required: true,
              message: 'Provider is required'
            }
          ]}
        >
          <Input maxLength={60} showCount placeholder="Enter provider" />
        </FormItem>

        <FormItem name="providerAddress" label="Provider Address">
          <Input
            maxLength={120}
            showCount
            placeholder="Enter provider address"
          />
        </FormItem>

        <FormItem
          name="description"
          label={t(serviceRecordConstants.DESCRIPTION_OF_SERVICE_RECORD)}
        >
          <TextArea
            rows={4}
            maxLength={5000}
            placeholder={t(serviceRecordConstants.DESCRIPTION_PLACEHOLDER)}
          />
        </FormItem>

        <FormItem name="startDate" label="Start date">
          <AntdDatePicker
            format="DD-MM-YYYY"
            style={{ width: '100%' }}
            placeholder="Select start date"
          />
        </FormItem>
        <FormItem name="documents" label="Documents">
          {isEditMode && existingDocumentsCount > 0 && (
            <InfoText>
              {existingDocumentsCount} existing document
              {existingDocumentsCount > 1 ? 's' : ''}. You can add{' '}
              {remainingSlots} more.
            </InfoText>
          )}
          <Upload
            fileList={fileList}
            beforeUpload={() => false}
            onChange={handleFileChange}
            accept=".pdf,.png,.jpg,.jpeg,.heic,.docm,.gif,.docx,.doc,.xlsx,.xls,.txt,.csv,.ppt,.pptx"
            maxCount={isEditMode ? remainingSlots : MAX_DOCUMENTS}
          >
            <Button
              disabled={
                isEditMode
                  ? fileList.length >= remainingSlots
                  : fileList.length >= MAX_DOCUMENTS
              }
            >
              <UploadOutlined />
              Upload document
              {isEditMode
                ? fileList.length > 0 &&
                  ` (${fileList.length}/${remainingSlots})`
                : fileList.length > 0 &&
                  ` (${fileList.length}/${MAX_DOCUMENTS})`}
            </Button>
          </Upload>
          {((isEditMode && fileList.length >= remainingSlots) ||
            (!isEditMode && fileList.length >= MAX_DOCUMENTS)) && (
            <ErrorText>
              {isEditMode
                ? `Maximum ${remainingSlots} additional document${remainingSlots !== 1 ? 's' : ''} allowed`
                : `Maximum ${MAX_DOCUMENTS} documents allowed`}
            </ErrorText>
          )}
        </FormItem>
        <Divider />
      </StyledForm>
    </Modal>
  )
}

const StyledForm = styled(AntdForm)`
  display: flex;
  flex-direction: column;
  gap: 16px;
`

const ModalFooter = styled.div`
  display: flex;
  gap: 16px;
  justify-content: flex-end;
`

const ErrorText = styled(Text)`
  color: #ff4d4f;
  font-size: 12px;
  margin-top: 4px;
  display: block;
`

const InfoText = styled(Text)`
  font-size: 12px;
  margin-bottom: 8px;
  display: block;
`

export default ServiceRecordModal
